import { supabase } from "../client"
import { getFormattedDate } from "../utils/formattedDate";
import { showToastFailure, showToastSuccess } from "./toast";
import axios from 'axios'

const createOrder = async (orderData, startLoader, stopLoader) => {
  const { user_id, pass_detail_id, amount, gst_amount, payment_status, created_at, no_of_ticket_purchased, payment_id, payment_mode, order_confirmed } = orderData;
  
  try {
    startLoader();

    // Insert query for the orders table
    const { data, error } = await supabase
      .from('orders')
      .insert([
        {
          user_id,
          pass_detail_id,
          amount,
          gst_amount,
          payment_status,
          created_at: getFormattedDate(new Date()),
          no_of_ticket_purchased,
          payment_id,
          payment_mode,
          order_confirmed
        }
      ])
      .select();

    if (error) {
      throw new Error('Failed to create order');
    }

    const insertedOrder = data[0];
    const { id: order_id } = insertedOrder;

    if (payment_status && payment_mode === 'online') {
      await createSession({ order_id, game_status: 'ongoing', created_at }, stopLoader);
      await calculateRemainingTickets(pass_detail_id, no_of_ticket_purchased);
    }

    if(orderData.ticketDetails?.length){
      await createOrderMetaData({order_id, ticketDetails: orderData.ticketDetails } , stopLoader)
    }

    if(payment_mode === 'cash' && data[0]){
      stopLoader();
      showToastSuccess('Your order has been successfully created! Please make your cash payment at the reception to schedule your session!');
    }

  } catch (error) {
    stopLoader();
    showToastFailure('Please contact the store if the payment is already processed, there is a technical issue');
  }
};

const calculateRemainingTickets = async (pass_detail_id, no_of_ticket_purchased) => {
 
  const { data } = await supabase
  .from('pass_details')
  .select('no_of_tickets')
  .eq('id', pass_detail_id)


  if (data && data.length && data[0].no_of_tickets >= no_of_ticket_purchased) {
    const remainingTickets = data[0].no_of_tickets - no_of_ticket_purchased;
   
    // Update the remaining tickets in the database
    const { error: updateError } = await supabase
      .from('pass_details')
      .update({ no_of_tickets: remainingTickets })
      .eq('id', pass_detail_id);

    if (updateError) {
      return
    }
  }
}

const createSession = async (sessionData , stopLoader) => {
  const { order_id, game_status } = sessionData;
  try {
    const { error } = await supabase
      .from('sessions')
      .insert([
        {
          order_id,
          game_status,
          created_at: getFormattedDate(new Date())
        }
      ]);

    if (error) {
      throw new Error('Failed to create session');
    }

    showToastSuccess('Your order has been placed successfully! Please visit the "View your active pass" section to check your ongoing sessions!');
    
  } catch (error) {
    showToastFailure('Payment was successful but there was a technical issue starting the session. Please contact the store.');
  }finally {
    stopLoader();
  }
};

const createOrderMetaData = async (metaData, stopLoader) => {
  const { order_id, ticketDetails } = metaData;
  try {
    // Create an array of objects to insert
    const dataToInsert = ticketDetails.map((ticket) => ({
      order_id,
      full_name: ticket.name,
      email: ticket.email,
      phone: ticket.mobile,
      dob: ticket.dob,
    }));

    // Insert the array into the order_metadata table
    const { error } = await supabase
      .from('order_metadata')
      .insert(dataToInsert);

    if (error) {
      throw new Error('Failed to insert order metadata');
    }
  } catch (error) {
    showToastFailure('Payment was successful, but there was an issue saving the ticket details. Please contact the store.');
  } finally {
    stopLoader();
  }
}

const createRazorPayOrder = async (totalAmt, userId, serviceId, showLoader, hideLoader) => {
  try {
    showLoader();
    const response = await axios.post(process.env.REACT_APP_API+'/create-order', {
      amount: totalAmt,
      currency: 'INR',
      notes: {
        user_id: userId,
        pass_detail_id: serviceId
      },
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const { order } = response.data;
    return order;
  } catch (error) {
    if (error.response) {
      // Server responded with a status other than 2xx
      console.error('Error response from server:', error.response.data);
    } else if (error.request) {
      // Request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something went wrong in setting up the request
      console.error('Error setting up request:', error.message);
    }
    console.error('Failed to create order:', error);
    throw error;
  } finally {
    hideLoader();
  }
};

const getOrderByUserId = async (userId, startLoader, stopLoader) => {
  try {
    startLoader();
    // Fetch orders with pass details and session data
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        pass_details:pass_detail_id (
          *
        ),
        sessions (
          *
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      

    if (error) {
      throw new Error('Failed to fetch orders');
    }

    return data;
  } catch (error) {
    showToastFailure('Failed to fetch orders');
    console.error(error);
    return [];
  } finally {
    stopLoader();
  }
};

const completeSession = async (feedbackData, startLoader, stopLoader) => {
  startLoader();
  try {

    const { error } = await supabase
      .from('sessions')
      .update({
        feedback: feedbackData.feedback,
        game_status: feedbackData.game_status,
        updated_at: new Date().toISOString(),
        updated_by: feedbackData?.userId,
        feedback_ambiance: feedbackData?.ambianceRating,
        feedback_games: feedbackData?.gamesRating,
        feedback_overall: feedbackData?.overAllRating,
      })
      .match({ id: feedbackData.sessionId}); // Update the session with the specific ID

    if (error) {
      throw new Error('Failed to complete the session');
    }
    
    if(feedbackData.game_status){
      showToastSuccess('Your session has been successfully completed! Thank you for booking with us. Don’t forget to check us out on social media for updates!');
    }else{
      showToastSuccess('Thank you for your valuable feedback! Your input helps us improve and deliver a better experience.');
    }
  } catch (error) {
    showToastFailure('There was a technical issue completing the session. Please try again later.');
  } finally {
    stopLoader();
  }
};



// eslint-disable-next-line import/no-anonymous-default-export
export default {createOrder, getOrderByUserId, completeSession, createOrderMetaData, createRazorPayOrder}; 